import React, { useEffect } from 'react'
import { useFormContext, useFieldArray } from 'react-hook-form'
import {
  Box,
  Button,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core'

import BannerHeadCard from '../BannerHeadCard'
import BannerUrlField from '../BannerUrlField'
import { AutoBlockSettings } from './components'

import helpers from 'helpers'

import theme from 'theme'

import useAuth from 'hooks/useAuth'
import useCookieBanner from 'hooks/useCookieBanner'
import useSnackbar from 'hooks/useSnackbar'

import * as service from 'service'
import constants from 'constants/index'

const BannerConfigurationCard = () => {
  const { register, errors, control, handleSubmit, setValue } = useFormContext()
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'urls',
  })
  const snackbar = useSnackbar()
  const { company, user } = useAuth()

  const { mixpanel } = helpers
  const { AUTO_BLOCK_FIXED_CATEGORIES, UNKNOWN_KEY } =
    constants.cookies.category

  const { banner, refreshBanner, nextStep, setIsSubmitting, setStepSubmit } =
    useCookieBanner()

  const addUrl = () => {
    append({ url: '', id: null })
  }

  const handleFormSubmit = async (data) => {
    const { autoBlockUnknown: savedAutoBlockUnknown } =
      helpers.cookies.banner.prepareFormDefaultValues(banner)

    try {
      setIsSubmitting(true)

      const bannerData = {
        name: data.name,
        status: data.status,
        privacyPolicyLink: data.privacyPolicyLink,
        cookiesPolicyLink: data.cookiesPolicyLink,
        domain: data.domain,
        urls: data.urls.filter((urlObj) => urlObj.url.trim() !== ''),
        autoBlockEnabled: true,
        autoBlockCategories: data.autoBlockUnknown
          ? [...AUTO_BLOCK_FIXED_CATEGORIES, UNKNOWN_KEY]
          : AUTO_BLOCK_FIXED_CATEGORIES,
      }

      await service.cookies.banner.updateConfiguration({
        bannerId: banner?.id,
        banner: bannerData,
      })

      if (data.autoBlockUnknown !== savedAutoBlockUnknown) {
        mixpanel.track(
          'Configurações-banner-Cookies',
          'Bloqueio por categoria',
          {
            action: 'definir-bloqueio-categoria-banner-cookies',
            companyId: company?.id,
            userId: user?.id,
          },
        )
      }

      snackbar.open({
        variant: 'success',
        message:
          constants.cookies.banner.SUCCESS_MESSAGES[
            constants.cookies.banner.STEPS.CONFIGURATION
          ],
      })

      await refreshBanner()
      nextStep()
    } catch (error) {
      setValue('autoBlockUnknown', savedAutoBlockUnknown)

      snackbar.open({
        variant: 'error',
        message:
          helpers.formatters.errorMessage(
            error?.response?.data?.error,
            false,
          ) || 'Erro ao salvar configuração. Tente novamente.',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  // Registra a função de submit no provider quando o componente é montado
  useEffect(() => {
    if (banner) {
      setStepSubmit(() => handleSubmit(handleFormSubmit)())
    }
  }, [setStepSubmit, handleSubmit, banner])

  return (
    <Box display="flex" flexDirection="column" gridGap={theme.spacing(3)}>
      <Paper
        variant="outlined"
        id={constants.cookies.banner.COOKIES_BANNER_SETTING_DRIVER_STEP_2}
      >
        <BannerHeadCard name="Configurações Básicas" />
        <Divider />
        <Box
          p={2}
          display="flex"
          flexDirection="column"
          gridGap={theme.spacing(2)}
        >
          {constants.cookies.banner.COOKIE_CREATION_CARD_ITEMS.map(
            (item, index) => (
              <TextField
                key={index}
                name={item.controlName}
                inputRef={register()}
                error={!!errors?.[item.controlName]}
                helperText={errors?.[item.controlName]?.message}
                label={item.label}
                type="text"
                color="primary"
                variant="outlined"
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
            ),
          )}
          {fields.map((field, index) => (
            <BannerUrlField
              key={field.id}
              field={field}
              index={index}
              errors={errors}
              remove={remove}
            />
          ))}
          <Grid container spacing={2}>
            <Grid item xs={12} md={10}>
              <Box
                display="flex"
                alignItems="center"
                gridGap={theme.spacing(2)}
              >
                <Box bgcolor={theme.palette.primary.light} padding={2}>
                  <Typography variant="h5" gutterBottom>
                    Possui mais de uma URL para o mesmo site?
                  </Typography>
                  <Typography>
                    Adicione as URLs do seu site para que o banner seja exibido
                    em todas elas. Não esqueça de variações como "www" e sem
                    "www". Exemplo: "https://www.site.com" e "https://site.com".
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={2}>
              <Box display="flex" justifyContent="flex-end">
                <Button
                  type="button"
                  variant="contained"
                  color="secondary"
                  onClick={addUrl}
                >
                  Adicionar URL
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
      <Paper variant="outlined">
        <BannerHeadCard name="Bloqueio automático de cookies" />
        <Divider />
        <AutoBlockSettings />
      </Paper>
    </Box>
  )
}

export default BannerConfigurationCard
